import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CreditCard, Clock, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import apiClient from "../lib/api";

const ALLOWED_PATHS = [
  "/dashboard/subscription",
  "/dashboard/profile",
];

const SubscriptionGuard = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("subscription");

  useEffect(() => {
    checkSubscription();
  }, [location.pathname]);

  const checkSubscription = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const response = await apiClient.get("/pharmacy/subscription");
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error("Error checking subscription:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-omu-blue-500"></div>
      </div>
    );
  }

  const isAllowedPath = ALLOWED_PATHS.some((path) => location.pathname.startsWith(path));

  if (loadError) {
    if (isAllowedPath) {
      return children;
    }
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 bg-slate-100">
              <RefreshCw className="h-10 w-10 text-slate-600" />
            </div>
            <CardTitle className="text-2xl font-heading">{t("loadErrorTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-600 text-center">{t("loadErrorDesc")}</p>
            <div className="flex flex-col gap-2">
              <Button className="w-full" onClick={checkSubscription}>
                {t("retry")}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate("/dashboard/subscription")}>
                {t("goSubscription")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const subscription = subscriptionStatus?.subscription;
  const hasPendingPayment = subscriptionStatus?.has_pending_payment;
  const isActive = subscription?.effective_status === "active";
  const isPending = subscription?.effective_status === "pending" || hasPendingPayment;
  const isExpired = subscription?.effective_status === "expired";

  if (isActive || isAllowedPath) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-0 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
            isPending ? "bg-amber-100" : "bg-red-100"
          }`}>
            {isPending ? (
              <Clock className="h-10 w-10 text-amber-600" />
            ) : (
              <AlertTriangle className="h-10 w-10 text-red-600" />
            )}
          </div>
          <CardTitle className="text-2xl font-heading">
            {isPending ? t("pendingTitle") : isExpired ? t("expiredTitle") : t("requiredTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {isPending ? (
            <Alert className="border-amber-200 bg-amber-50">
              <Clock className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">{t("pendingDesc")}</AlertDescription>
            </Alert>
          ) : isExpired ? (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {t("expiredDesc", { date: subscription?.end_date_formatted || "—" })}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-slate-200 bg-slate-50">
              <AlertTriangle className="h-4 w-4 text-slate-600" />
              <AlertDescription className="text-slate-700">{t("inactiveDesc")}</AlertDescription>
            </Alert>
          )}

          {subscription && (
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("plan")}:</span>
                <span className="font-medium">{subscription.plan_name || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("status")}:</span>
                <span className={`font-medium ${
                  isPending ? "text-amber-600" : isExpired ? "text-red-600" : "text-slate-600"
                }`}>
                  {isPending ? t("statusPending") : isExpired ? t("statusExpired") : t("statusInactive")}
                </span>
              </div>
              {subscription.end_date_formatted && (
                <div className="flex justify-between">
                  <span className="text-slate-500">{t("expiryDate")}:</span>
                  <span className="font-medium">{subscription.end_date_formatted}</span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <Button
              className="w-full bg-omu-blue-500 hover:bg-omu-blue-600 h-12 text-lg"
              onClick={() => navigate("/dashboard/subscription")}
            >
              <CreditCard className="h-5 w-5 mr-2" />
              {isExpired ? t("renew") : isPending ? t("viewPayments") : t("subscribe")}
            </Button>

            {!isPending && (
              <p className="text-center text-sm text-slate-500">{t("needHelp")}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionGuard;
